/**
 * Раскладки окон просмотра — то, что у Hikvision (iVMS-4200, веб-интерфейс NVR)
 * называется «разделением экрана»: 1, 4, 1+5, 9, 16.
 *
 * Раскладка — это сетка `cols × rows` и список окон на ней в единицах сетки. «1+5» —
 * сетка 3×3, где первое окно занимает угол 2×2, а пять остальных обходят его по краю:
 * одна камера крупно (вход), пять рядом мелко — самый частый вид у охраны.
 *
 * Окна хранятся отдельно от раскладки массивом из `MAX_WINDOWS` ячеек: переключение
 * 16 → 4 показывает первые четыре и НЕ забывает остальные двенадцать, а обратное
 * переключение возвращает их на места — как в регистраторе.
 */
export type LayoutId = '1' | '4' | '6' | '9' | '16';

export const LAYOUT_IDS: readonly LayoutId[] = ['1', '4', '6', '9', '16'];

export type Cell = { x: number; y: number; w: number; h: number };

export type Layout = { cols: number; rows: number; cells: Cell[] };

export const MAX_WINDOWS = 16;

function grid(n: number): Layout {
  const cells: Cell[] = [];
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      cells.push({ x, y, w: 1, h: 1 });
    }
  }
  return { cols: n, rows: n, cells };
}

export const LAYOUTS: Readonly<Record<LayoutId, Layout>> = {
  '1': grid(1),
  '4': grid(2),
  '6': {
    cols: 3,
    rows: 3,
    cells: [
      { x: 0, y: 0, w: 2, h: 2 },
      { x: 2, y: 0, w: 1, h: 1 },
      { x: 2, y: 1, w: 1, h: 1 },
      { x: 0, y: 2, w: 1, h: 1 },
      { x: 1, y: 2, w: 1, h: 1 },
      { x: 2, y: 2, w: 1, h: 1 },
    ],
  },
  '9': grid(3),
  '16': grid(4),
};

export function isLayoutId(value: unknown): value is LayoutId {
  return (
    typeof value === 'string' &&
    (LAYOUT_IDS as readonly string[]).includes(value)
  );
}

export function windowCount(layout: LayoutId): number {
  return LAYOUTS[layout].cells.length;
}

/** Раскладка, в которую все камеры влезают сразу, — для первого открытия. */
export function layoutForCount(count: number): LayoutId {
  if (count <= 1) {
    return '1';
  }
  if (count <= 4) {
    return '4';
  }
  if (count <= 6) {
    return '6';
  }
  return count <= 9 ? '9' : '16';
}

export type Slots = (string | null)[];

export function emptySlots(): Slots {
  return Array.from({ length: MAX_WINDOWS }, () => null);
}

/** Привести сохранённое к ровно `MAX_WINDOWS` ячейкам — хранилище переживает версии. */
export function normalizeSlots(value: unknown): Slots {
  const out = emptySlots();
  if (Array.isArray(value)) {
    value.slice(0, MAX_WINDOWS).forEach((v, i) => {
      out[i] = typeof v === 'string' && v.length > 0 ? v : null;
    });
  }
  return out;
}

/**
 * Камеру — в окно. Если она уже открыта в другом окне, она ПЕРЕЕЗЖАЕТ, а не
 * раздваивается: две сессии к одной камере — это двойной поток с объекта ради одной и
 * той же картинки.
 */
export function assign(slots: Slots, index: number, cameraId: string): Slots {
  const next = slots.map(id => (id === cameraId ? null : id));
  next[index] = cameraId;
  return next;
}

export function clearSlot(slots: Slots, index: number): Slots {
  const next = slots.slice();
  next[index] = null;
  return next;
}

/**
 * Куда встать выделению после того, как камера открыта в окне `from`: в следующее
 * ПУСТОЕ окно раскладки, а если пустых нет — просто в следующее по кругу. Так щелчки
 * по дереву раскладывают камеры по окнам подряд, как в iVMS.
 */
export function nextWindow(slots: Slots, count: number, from: number): number {
  for (let step = 1; step <= count; step += 1) {
    const i = (from + step) % count;
    if (!slots[i]) {
      return i;
    }
  }
  return (from + 1) % count;
}

export function pageCount(total: number, count: number): number {
  return Math.max(1, Math.ceil(total / count));
}

/**
 * Страница `page` списка камер — в окна раскладки. Остальные ячейки очищаются: окна
 * за пределами раскладки не играют, но и чужую страницу помнить не должны.
 */
export function fillPage(
  cameraIds: readonly string[],
  count: number,
  page: number,
): Slots {
  const out = emptySlots();
  cameraIds.slice(page * count, page * count + count).forEach((id, i) => {
    out[i] = id;
  });
  return out;
}

/** Окно перетащили на другое — они меняются камерами, как в iVMS. */
export function swap(slots: Slots, a: number, b: number): Slots {
  const next = slots.slice();
  next[a] = slots[b];
  next[b] = slots[a];
  return next;
}

/**
 * Соседнее окно для стрелок клавиатуры: ближайшее, чей центр лежит в нужную сторону.
 * Считается по геометрии, а не по номеру, — в «1+5» справа от большого окна стоят
 * два, и «вниз» из правого верхнего должно вести в правое среднее, а не в «следующее».
 */
export function neighbour(
  layout: LayoutId,
  from: number,
  dir: 'left' | 'right' | 'up' | 'down',
): number {
  const cells = LAYOUTS[layout].cells;
  const c = cells[from];
  if (!c) {
    return 0;
  }
  const cx = c.x + c.w / 2;
  const cy = c.y + c.h / 2;
  let best = from;
  let bestDist = Infinity;
  cells.forEach((o, i) => {
    if (i === from) {
      return;
    }
    const ox = o.x + o.w / 2;
    const oy = o.y + o.h / 2;
    const dx = ox - cx;
    const dy = oy - cy;
    const ahead =
      dir === 'left'
        ? dx < 0
        : dir === 'right'
        ? dx > 0
        : dir === 'up'
        ? dy < 0
        : dy > 0;
    if (!ahead) {
      return;
    }
    // Вдоль направления — как есть, поперёк — дороже: «вправо» не уходит по диагонали.
    const along =
      dir === 'left' || dir === 'right' ? Math.abs(dx) : Math.abs(dy);
    const across =
      dir === 'left' || dir === 'right' ? Math.abs(dy) : Math.abs(dx);
    const dist = along + across * 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  });
  return best;
}
